"use client";

import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import ShadowCard from "@/components/common/ShadowCard";
import Button from "@/components/ui/Button";
import Input from "@/components/ui/Input";
import { validateUSPhoneNumber } from "@/utils/validation";

interface ScheduleCallModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function ScheduleCallModal({
  isOpen,
  onClose,
}: ScheduleCallModalProps) {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [mounted, setMounted] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    services: "",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [phoneError, setPhoneError] = useState("");

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    // Clear phone error when user types
    if (name === "phone") {
      setPhoneError("");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess(false);

    if (!executeRecaptcha) {
      setError("reCAPTCHA not loaded. Please try again.");
      setLoading(false);
      return;
    }

    // Validate phone number
    if (!validateUSPhoneNumber(formData.phone)) {
      setPhoneError("Please enter a valid US phone number (10 digits)");
      setLoading(false);
      return;
    }

    try {
      // Generate reCAPTCHA token
      const recaptchaToken = await executeRecaptcha("schedule_call_form");

      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...formData,
          sourceUrl: window.location.href,
          recaptchaToken,
        }),
      });

      if (response.ok) {
        setSuccess(true);
        setTimeout(() => {
          onClose();
          setSuccess(false);
          setFormData({ name: "", email: "", phone: "", services: "" });
        }, 5000);
      } else {
        const data = await response.json();
        setError(data.error || "Something went wrong. Please try again.");
      }
    } catch (err) {
      setError("Failed to submit. Please check your connection.");
    } finally {
      setLoading(false);
    }
  };

  if (!mounted) return null;

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-9999 flex items-center justify-center p-4 sm:p-6">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          />

          {/* Modal Content */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="relative z-10 w-full max-w-5xl"
          >
            <ShadowCard className="flex flex-col overflow-hidden border-red-500/50 bg-linear-to-br from-[#EE4137] to-[#A10900] p-0 shadow-[0_0_50px_-5px_rgba(220,38,38,0.5)] md:flex-row md:p-5">
              {/* Desktop Left Image Section */}
              <div
                className="hidden w-1/2 overflow-hidden rounded-xl bg-cover bg-center md:block"
                style={{ backgroundImage: "url('/popup-cover.webp')" }}
              >
                <div className="h-full w-full bg-black/20" />
              </div>

              {/* Form Section */}
              <div className="relative flex w-full flex-col p-6 md:w-1/2 md:p-10">
                <button
                  onClick={onClose}
                  className="absolute top-4 right-4 cursor-pointer text-white/80 transition-colors hover:text-white"
                >
                  <X size={24} />
                </button>

                <div className="mb-0">
                  <h2 className="font-raleway mb-2.5 text-2xl font-bold text-white md:text-[2rem]">
                    Schedule a Call
                  </h2>
                  <p className="font-raleway mb-5 text-base leading-[1.3] font-normal text-white/90">
                    A quick, no-obligation conversation to answer your
                    questions.
                  </p>
                </div>

                <div className="relative">
                  {/* Success Message Overlay */}
                  {success && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
                      <h3 className="font-raleway mb-2 text-2xl font-bold text-white">
                        Message Sent!
                      </h3>
                      <p className="font-raleway text-white/90">
                        We will get back to you shortly.
                      </p>
                    </div>
                  )}

                  {/* Form - Hidden but takes space when success is true */}
                  <form
                    onSubmit={handleSubmit}
                    className={`space-y-5 transition-opacity duration-300 ${
                      success ? "invisible opacity-0" : "visible opacity-100"
                    }`}
                  >
                    <Input
                      type="text"
                      name="name"
                      label="Name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="Enter your full name"
                    />

                    <Input
                      type="email"
                      name="email"
                      label="Email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="Enter your email address"
                    />

                    <Input
                      type="tel"
                      name="phone"
                      label="Phone number"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      placeholder="Enter your phone number"
                    />
                    {phoneError && (
                      <p className="text-sm font-semibold text-red-200">
                        {phoneError}
                      </p>
                    )}

                    <Input
                      type="text"
                      name="services"
                      label="Services"
                      value={formData.services}
                      onChange={handleChange}
                      required
                      placeholder="Enter services you are interested in"
                    />

                    {error && (
                      <p className="rounded bg-red-500/50 p-2 text-sm font-semibold text-white">
                        {error}
                      </p>
                    )}

                    <div className="flex justify-center pt-4">
                      <Button
                        variant="white"
                        className="md:w-full"
                        disabled={loading}
                      >
                        {loading ? "Sending..." : "Submit"}
                      </Button>
                    </div>
                  </form>
                </div>
              </div>
            </ShadowCard>
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body
  );
}
